// `/` — THE PAID FLOW STARTS HERE: the address comes first, before an account and before payment.
//
// That order is the prototype's and it is kept: an account wall in front of someone who has not seen a number yet is
// the cheapest way to lose them. What follows the address is not the client's to decide — the typed text goes to
// `v2ResolveProperty`, and where the owner goes next is what its decisions answered (`whereNext`, `./gates.tsx`).
//
// WEB-PARITY-2B: THIS IS THE APP'S "START YOUR APPEAL" SCREEN (AppealEntryFlow.swift:204-257, 600-716). Its hero (EN-1);
// its address card, with the app's placeholder and its "Not in the list?" line (EN-3); the unit field with the footer
// that states the requirement (EN-4); and, on the same screen as the app has it, "What kind of property?" with the app's
// seven labels and the line saying what a credit covers (EN-2, PR-1, PR-2). Every word is the app's, from
// `components/prePayCopy.ts`, pinned to the Swift by `web/test/prePayCopy.test.tsx`.
//
// THE TYPE IS SENT WITH THE ADDRESS, and the SERVER judges it. The app stops an unsupported pick the moment it is made,
// by its own rule (`PropertyTypeSupport`); here that rule is G-5's, so the stop comes back with the answer: the app's
// sheet title for G-5's refusal (PR-3), G-5's own sentence, and "Choose a different property" (PR-4),
// which clears the pick as the app's does. A condominium with no unit (G-4) and an address with no state the server
// could find (G-1 `unknownState`) are answered here too, each in the server's own sentence, because the owner fixes both
// in these boxes.
//
// WEB-PAY-2: THE FREE CHECK IS THE SITE'S, and what comes BACK from it is an address and a unit on the URL
// (`../app/handIn.ts`), which fill the two boxes and do nothing else: nothing is sent until the owner presses the button,
// and then exactly what the boxes say. THE UNIT BOX (R162) keeps the app's own label, pinned by `web/test/entry.test.tsx`.
// THE ADDRESS BOX SUGGESTS (WEB-SUGGEST): Apple's suggestions through `components/AddressCombobox.tsx`; a hand-in still
// wins, and a pick never wipes the Unit box.

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { PropertyTypeCode, ResolvePropertyResponse } from "@contract";
import { UNIT_LABEL, readHandIn, unitOrNull } from "../app/handIn";
import { useSession } from "../app/session";
import { useAction } from "../app/useCall";
import { AddressCombobox } from "../components/AddressCombobox";
import { CopyLine, CopyNote } from "../components/copy";
import { Actions } from "../components/layout";
import { prePayText, type PrePayId } from "../components/prePayCopy";
import { Refusal } from "../components/Refusal";
import { SCENARIO_ORDER, SCENARIOS } from "../transport/fake/scenarios";
import { PRODUCTION_MODE } from "../app/buildModes";
import { whereNext } from "./nextStep";

/** The app's picker, in its order: the label it shows, and the contract's code sent for it (PR-1). The four served
 *  types first (`PropertyTypeSupport.supported`), then the three the app lists after them (AEF:221-223). */
const KINDS: readonly { readonly code: PropertyTypeCode; readonly label: PrePayId }[] = [
  { code: "Single-family", label: "entry.kind.singleFamily" },
  { code: "Twin/Semi-detached", label: "entry.kind.twin" },
  { code: "Townhouse", label: "entry.kind.townhouse" },
  { code: "Condo", label: "entry.kind.condo" },
  { code: "Multi-unit", label: "entry.kind.multiUnit" },
  { code: "Manufactured", label: "entry.kind.manufactured" },
  { code: "Other", label: "entry.kind.other" },
];

/** The refusal sheet's title for each of G-5's refusals (PropertyTypeSupport.swift:47-53). */
const REFUSAL_TITLE = {
  manufactured: "refusal.manufactured.title",
  multiUnit: "refusal.multiUnit.title",
  unclassified: "refusal.unclassified.title",
} as const satisfies Record<string, PrePayId>;

export function Entry() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  // THE HAND-IN IS READ ONCE, INTO THE BOXES' OWN STATE — never into the session, which holds only what the owner has
  // SENT. Without one, the boxes show what this tab last sent, as they always did.
  const [handIn] = useState(() => readHandIn(location.search));
  const [typed, setTyped] = useState(handIn.address ?? session.typedAddress);
  const [unit, setUnit] = useState(handIn.unit ?? session.typedUnit);
  // The picker opens on the pick this tab last sent for this address, if any (PR-5), as the app's draft keeps it.
  const [kind, setKind] = useState<PropertyTypeCode | "">(session.propertyType ?? "");
  // The answer this screen is showing a stop from, if any: a refusal, or a sentence the owner fixes here.
  const [answer, setAnswer] = useState<ResolvePropertyResponse | null>(null);
  const kindBox = useRef<HTMLSelectElement>(null);

  // …and the query comes off the address bar, so the address is not kept in the tab's history or a copied link.
  const hasQuery = location.search !== "";
  useEffect(() => {
    if (hasQuery) navigate({ pathname: location.pathname, search: "" }, { replace: true });
  }, [hasQuery, location.pathname, navigate]);

  // THE SESSION NAMES THE APPEAL, OR NONE (WEB-APPEAL-ID): the first resolve sends no id, and the session adopts the
  // one the server mints, so every screen after this names it.
  const resolve = useAction((input: { address: string; unit: string; kind: PropertyTypeCode | "" }) =>
    session.resolveAddress({
      typedAddress: input.address,
      unit: unitOrNull(input.unit),
      ...(input.kind === "" ? {} : { propertyType: input.kind }),
    }),
  );

  const submit = async (address: string, unitTyped: string) => {
    const trimmed = address.trim();
    if (trimmed === "") return;
    session.setTypedAddress(trimmed);
    session.setTypedUnit(unitTyped.trim());
    setAnswer(null);
    const result = await resolve.run({ address: trimmed, unit: unitTyped, kind });
    if (!result.ok) return;
    const next = whereNext(result.response);
    if (next === null) setAnswer(result.response);
    else navigate(next);
  };

  const g1 = answer?.decisions["G-1"];
  const g4 = answer?.decisions["G-4"];
  const g5 = answer?.decisions["G-5"];
  const unknownState = g1 !== undefined && g1.ran && g1.outcome === "block" ? g1.copy : null;
  const unitMissing = g4 !== undefined && g4.ran && g4.outcome === "block" ? g4.copy : null;
  const refused = g5 !== undefined && g5.ran && g5.outcome === "block" && g5.value.refusal !== null
    ? { kind: g5.value.refusal, title: REFUSAL_TITLE[g5.value.refusal], copy: g5.copy }
    : null;
  // AMBER MEANS UNMET (Models.swift `unitFieldFooter`, AEF:692-700): the footer names the requirement for a condominium,
  // and is amber only while the unit is still empty.
  const unitRequired = kind === "Condo";
  const unitUnmet = unitRequired && unit.trim() === "";

  return (
    <>
      <p className="eyebrow" data-app-copy="entry.navTitle">{prePayText("entry.navTitle")}</p>
      <h1 data-app-copy="entry.hero.title">{prePayText("entry.hero.title")}</h1>
      <h2 className="sub" data-app-copy="entry.hero.body">{prePayText("entry.hero.body")}</h2>

      <form
        className="entry"
        onSubmit={(e) => {
          e.preventDefault();
          void submit(typed, unit);
        }}
      >
        <div className="card entry-card">
          <AddressCombobox
            suggester={session.addressSuggester}
            label={prePayText("entry.address.title")}
            placeholder={prePayText("entry.address.placeholder")}
            value={typed}
            onChange={setTyped}
            unit={unit}
            onUnitChange={setUnit}
            hint={<span data-app-copy="entry.address.hint">{prePayText("entry.address.hint")}</span>}
            warm
          />
          {unknownState !== null && <CopyNote copy={unknownState} tone="stop" />}
          <label className="field unit-field">
            <span>{UNIT_LABEL}</span>
            <input type="text" value={unit} autoComplete="off" onChange={(e) => setUnit(e.currentTarget.value)} />
          </label>
          <p
            className={unitUnmet ? "field-foot unmet" : "field-foot"}
            data-app-copy={unitRequired ? "entry.unit.required" : "entry.unit.optional"}
          >
            {prePayText(unitRequired ? "entry.unit.required" : "entry.unit.optional")}
          </p>
          {unitMissing !== null && <CopyNote copy={unitMissing} tone="stop" />}
        </div>

        <div className="card entry-card">
          <label className="field">
            <span data-app-copy="entry.kind.title">{prePayText("entry.kind.title")}</span>
            <select
              ref={kindBox}
              value={kind}
              onChange={(e) => {
                setKind(e.currentTarget.value as PropertyTypeCode | "");
                setAnswer(null);
              }}
            >
              <option value="">{prePayText("entry.kind.select")}</option>
              {KINDS.map((k) => (
                <option key={k.code} value={k.code} data-app-copy={k.label}>
                  {prePayText(k.label)}
                </option>
              ))}
            </select>
          </label>
          <p className="field-foot" data-app-copy="entry.kind.coverage">{prePayText("entry.kind.coverage")}</p>
        </div>

        {refused !== null && (
          <section className="sheet stop" role="alert" aria-labelledby="refusal-title" data-refusal={refused.kind}>
            <h2 id="refusal-title" data-app-copy={refused.title}>{prePayText(refused.title)}</h2>
            <CopyLine copy={refused.copy} />
            {/* The app's sheet repeats the coverage line under the refusal; here it is the line just above, in the picker's
                own card, so it is not said twice on one screen. */}
            <Actions>
              <button
                className="btn primary"
                type="button"
                data-app-copy="refusal.choose"
                onClick={() => {
                  // Clear the choice so nothing can go on with a refused type (AEF:276-278), and put the owner back on it.
                  setKind("");
                  setAnswer(null);
                  kindBox.current?.focus();
                }}
              >
                {prePayText("refusal.choose")}
              </button>
            </Actions>
          </section>
        )}

        <Actions>
          {/* While a refusal is showing, its "Choose a different property" is the next thing to do (the app's sheet covers
              its CTA), so this steps back. */}
          <button className={refused === null ? "btn primary" : "btn ghost"} type="submit" disabled={resolve.state.status === "pending"}>
            {resolve.state.status === "pending" ? "Checking…" : "Check this address"}
          </button>
        </Actions>
      </form>

      {resolve.state.status === "failed" && <Refusal failure={resolve.state.failure} context="This address" />}

      {/* The fake's sample addresses, on the fake only. THE MODE IS WRITTEN OUT (B8): in the production build it is the
          literal "production", so this whole branch is dead code and the bundler drops it — and with it the fake's
          scenarios, which the production bundle then never carries. `session.onFake` is never true there anyway. */}
      {import.meta.env.MODE !== PRODUCTION_MODE && session.onFake && (
        <>
          <p className="faint" style={{ margin: "26px 0 8px" }}>
            This build talks to a fake service. Three addresses it knows:
          </p>
          <div className="picks">
            {SCENARIO_ORDER.map((id) => {
              const scenario = SCENARIOS[id];
              return (
                <button
                  key={id}
                  type="button"
                  className="pick"
                  onClick={() => {
                    setTyped(scenario.typedAddress);
                    setUnit("");
                    void submit(scenario.typedAddress, "");
                  }}
                >
                  <span>
                    <b>{scenario.label}</b>
                    <em>{scenario.sublabel}</em>
                  </span>
                  <span className="tag">{scenario.tag}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

/**
 * `/free-check` — THE ONE FREE CHECK IS THE SITE'S (R151), so this route leaves for it. It renders nothing: the page it
 * hands to says everything, and a sentence here would be a new one nobody approved.
 */
export function FreeCheckHandoff({ to }: { to: string }) {
  const { leave } = useSession();
  // Once per mount: a re-render (a session change, React's development double-effect) must not navigate twice.
  const left = useRef(false);
  useEffect(() => {
    if (left.current) return;
    left.current = true;
    leave(to);
  }, [leave, to]);
  return null;
}
